import {Hook} from "../lib/Hook.js";

export class TimesheetEntryLiveService {

    /**
     * Get informed before the service refreshed by an api.
     * This call will be synchronously block the next refresh...
     * @type {Hook}
     */
    aboutToRefresh = new Hook();

    refreshed = new Hook();

    /**
     * @private {TimesheetEntryRepository}
     */
    _entryRepo;

    /**
     * @private {ReactiveArray<TimesheetEntry>}
     */
    _entries;

    /**
     * How often poll the repo in seconds
     *
     * @type {number}
     * @private
     */
    _pollingInterval = 20;

    _apiPollingTimerId;

    _clockTimerId;

    /**
     * @private {Date}
     */
    _start;

    /**
     * @private {Date}
     */
    _end;

    /**
     * @param  {TimesheetEntryRepository} entryRepo
     * @param {ReactiveArray<TimesheetEntry>} entries
     * @param {number} pollingInterval in seconds
     */
    constructor(entryRepo, entries, pollingInterval=20) {
        this._entryRepo = entryRepo;
        this._entries = entries;
        this._pollingInterval = pollingInterval;
    }

    /**
     * @return {Date[]}
     */
    getTimeRange() {
        if (!this._start || !this._end) {
            return [];
        }
        return [this._start, this._end];
    }

    /**
     * @param {Date} start
     * @param {Date} end
     */
    setTimeRange(start, end) {
        this._start = start;
        this._end = end;
        if (this.isListening()) {
            this.stopListening();
            this._entries.clear();
            this.startListening();
        }
    }

    /**
     * Start listening to current entry changes
     * @return {void}
     */
    startListening() {
        if (this._apiPollingTimerId) {
            return;
        }
        if (!this._start || !this._end) {
            throw new Error('You have to pass a timeRange before listening');
        }
        this._refreshFromRepo();
        this._apiPollingTimerId = setInterval(() => {
            this._refreshFromRepo();
        }, this._pollingInterval*1000)

    }

    /**
     @return {void}
     */
    stopListening() {
        if (this._apiPollingTimerId) {
            clearInterval(this._apiPollingTimerId);
            this._apiPollingTimerId = undefined;
        }
        this._stopClockTimer();
    }

    /**
     * @return {boolean}
     */
    isListening() {
        return !!this._apiPollingTimerId;
    }

    /**
     * Trigger a refresh manually
     */
    refresh() {
        this._refreshFromRepo();
    }

    _refreshFromRepo() {

        this.aboutToRefresh.fire();

        this._entryRepo.getDuring(this._start, this._end).then(entries => {

            if (!this._timeRangeContainsToday()) {
                this._stopClockTimer();
                this._entries.fill(...entries);
                this.refreshed.fire();
                return;
            }
            this._startClockTimer();
            this._refreshRunningEntries(entries);

        });

    }

    /**
     * We pass initial entries here to avoid jumping stats and values by multiple requests.
     *
     * @param {TimesheetEntry[]} initialEntries
     * @private
     */
    _refreshRunningEntries(initialEntries=[]) {
        this._entryRepo.getRunningEntries().then(entries => {

            if (entries.length === 0) {
                console.info('No running entry...cancelling');
                if (initialEntries.length) {
                    this._entries.fill(...initialEntries);
                }
                this.refreshed.fire();
                return;
            }

            // I only support one entry
            /** @type {TimesheetEntry} **/
            let entry = entries[0];

            console.info('Running entry found...updating');

            if (initialEntries.length) {
                initialEntries.push(entry);
                this._entries.fill(...initialEntries);
                this.refreshed.fire();
                return;
            }

            let idx = this._entries.findIndexBy({id: entry.id});

            if (idx === -1) {
                this._entries.push(entry);
            }

            this.refreshed.fire();

        });
    }

    _updateCurrentEntry() {
        let idx = this._entries.findIndex(item => !item.isLogged);
        if (idx === -1) {
            return;
        }
        let entry = this._entries.at(idx);
        entry.duration = entry.duration+1;
        this._entries.update(idx, entry);
    }

    _timeRangeContainsToday() {
        if (!this._start || !this._end) {
            return false;
        }
        let today = (new Date()).getTime();
        return this._start.getTime() < today && this._end.getTime() > today;
    }

    _startClockTimer() {
        if (this._clockTimerId) {
            return;
        }
        this._clockTimerId = setInterval(() => {
            this._updateCurrentEntry();
        }, 1000);
    }

    _stopClockTimer() {
        if (this._clockTimerId) {
            clearInterval(this._clockTimerId);
            this._clockTimerId = undefined;
        }
    }
}